import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { eur } from "@/components/excelVoce/format";
import { ConfrontoCards } from "./ConfrontoCards";

const Th = ({ children, right }) => <th className={`py-2 px-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500 ${right ? "text-right" : "text-left"}`}>{children}</th>;
const K = ["materiali", "manodopera", "noli"];

const Cella = ({ c }) => (
  <td className="py-1.5 px-2 text-right font-mono text-[11px] whitespace-nowrap">
    <div className="text-slate-500">{eur(c.previsto)}</div>
    <div className={Number(c.effettivo) ? "text-sky-800" : "text-slate-300"}>{eur(c.effettivo)}</div>
    {c.da_determinare > 0 && <div className="text-rose-700 font-sans">+ {c.da_determinare} da det.</div>}
  </td>
);

const Scost = ({ c, attivo }) => {
  if (!attivo) return <td className="py-1.5 px-2 text-right text-xs text-slate-300">—</td>;
  if (c.scostamento === null) return <td className="py-1.5 px-2 text-right text-[11px] text-rose-700">non calcolabile</td>;
  const sc = Number(c.scostamento);
  return <td className={`py-1.5 px-2 text-right font-mono text-xs font-semibold ${sc > 0.004 ? "text-rose-700" : "text-emerald-700"}`}>{eur(sc)}</td>;
};

const RigaCategoria = ({ cat }) => (
  <tr className="bg-amber-50/70 dark:bg-amber-950/20 border-y border-amber-200 font-semibold" data-testid={`confronto-cat-${cat.capitolo_id}`}>
    <td className="py-1.5 px-2 text-xs" colSpan={2}>{cat.categoria} <span className="font-normal text-slate-500">· {cat.n_voci} voci</span></td>
    <td className="py-1.5 px-2 text-right font-mono text-xs">{eur(cat.ricavo)}</td>
    {K.map((k) => <Cella key={k} c={cat.confronto[k]} />)}
    <Cella c={cat.confronto.totale} />
    <Scost c={cat.confronto.totale} attivo={Number(cat.confronto.totale.effettivo) > 0 || cat.confronto.totale.da_determinare > 0} />
  </tr>
);

export const ConfrontoComputo = ({ computoId }) => {
  const [d, setD] = useState(null);
  const [soloEff, setSoloEff] = useState(true);
  useEffect(() => { api.get(`/computi/${computoId}/registro-effettivo/confronto`).then((r) => setD(r.data)); }, [computoId]);
  if (!d) return <div className="text-slate-500 p-4">Caricamento…</div>;
  const m = d.materiali;
  const voci = (capId) => d.voci.filter((v) => v.capitolo_id === capId && (!soloEff || v.ha_effettivo));
  return (
    <Card data-testid="confronto-computo"><CardContent className="p-4 sm:p-6 space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Controllo costi · previsto (analisi Excel) e effettivo (registro unico)</div>
        <p className="text-xs text-slate-500 mt-0.5">Importo contrattuale {eur(d.ricavo)} · materiali conteggiati solo se utilizzati · manodopera secondo il criterio per voce ed esecutore · i costi da determinare non valgono € 0 e rendono lo scostamento non calcolabile.</p>
      </div>
      <ConfrontoCards confronto={d.totale} testid="computo-confronto" />
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600" data-testid="computo-extra">
        <span>Ordinato: {eur(m.ordinato.importo)}</span><span>Consegnato: {eur(m.consegnato)}</span>
        <span className="text-amber-700">Giacenza materiali: {eur(m.giacenza)}</span>
        <span className="text-amber-700">Noli da attribuire: {eur(d.non_attribuito.noli)}</span>
        <span className="text-amber-700">Ore da attribuire: {eur(d.non_attribuito.manodopera_ore)}</span>
      </div>
      <label className="flex items-center gap-1.5 text-xs"><Checkbox checked={soloEff} onCheckedChange={(v) => setSoloEff(!!v)} data-testid="confronto-solo-effettivo" /> Mostra solo le voci con registrazioni effettive (i subtotali restano completi)</label>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="confronto-voci-table">
          <thead className="border-b"><tr><Th>N.Ord</Th><Th>Voce / categoria</Th><Th right>Importo contr.</Th><Th right>Materiali prev. / eff.</Th><Th right>Manodopera prev. / eff.</Th><Th right>Noli prev. / eff.</Th><Th right>Totale prev. / eff.</Th><Th right>Scostamento</Th></tr></thead>
          <tbody>
            {d.per_categoria.map((cat) => (
              <Fragment key={cat.capitolo_id}>
                <RigaCategoria cat={cat} />
                {voci(cat.capitolo_id).map((v) => (
                  <tr key={v.riga_id} className="border-b border-slate-100" data-testid={`confronto-voce-${v.num_ord}`}>
                    <td className="py-1.5 px-2 font-mono text-xs">{v.num_ord}</td>
                    <td className="py-1.5 px-2 text-xs"><span className="font-mono text-slate-500">{v.tariffa}</span> {v.descrizione.slice(0, 80)}</td>
                    <td className="py-1.5 px-2 text-right font-mono text-xs">{eur(v.ricavo)}</td>
                    {K.map((k) => <Cella key={k} c={v.confronto[k]} />)}
                    <Cella c={v.confronto.totale} />
                    <Scost c={v.confronto.totale} attivo={v.ha_effettivo} />
                  </tr>
                ))}
              </Fragment>
            ))}
            <tr className="border-t-2 border-slate-400 font-semibold" data-testid="confronto-totale-generale">
              <td className="py-2 px-2 text-xs" colSpan={2}>TOTALE GENERALE</td>
              <td className="py-2 px-2 text-right font-mono text-xs">{eur(d.ricavo)}</td>
              {K.map((k) => <Cella key={k} c={d.totale[k]} />)}
              <Cella c={d.totale.totale} />
              <Scost c={d.totale.totale} attivo />
            </tr>
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
};
